// src/pages/StorePage.js
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function StorePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('coupons');
  const [redeemed, setRedeemed] = useState({});
  const [redeeming, setRedeeming] = useState(null);

  useEffect(() => {
    api.getStore(id).then(store => {
      setStore(store);
      api.trackEvent('STORE_VIEW', id);
    }).finally(() => setLoading(false));
  }, [id]);

  const openMaps = () => {
    if (!store) return;
    const url = /iPhone|iPad|iPod/i.test(navigator.userAgent)
      ? `maps://maps.apple.com/?ll=${store.latitude},${store.longitude}&q=${encodeURIComponent(store.name)}`
      : `https://www.google.com/maps/search/?api=1&query=${store.latitude},${store.longitude}`;
    window.open(url, '_blank');
  };

  const redeem = async (couponId) => {
    if (!user) { navigate('/auth?redirect=' + encodeURIComponent(window.location.pathname)); return; }
    setRedeeming(couponId);
    try {
      await api.redeemCoupon(couponId);
      api.trackEvent('DEAL_LOCK', store.id, null, couponId);
      setRedeemed(prev => ({ ...prev, [couponId]: true }));
    } catch (err) { alert(err.message); }
    finally { setRedeeming(null); }
  };

  if (loading) return <div className="page-loading"><div className="spinner" /></div>;
  if (!store) return <div className="page-error">Store not found.</div>;

  return (
    <div className="detail-root">
      <div className="detail-hero store-hero-tall" style={{ backgroundImage: store.imageUrl ? `url(${store.imageUrl})` : undefined }}>
        {!store.imageUrl && <div className="detail-hero-placeholder">🏪</div>}
        <div className="detail-hero-top">
          <button className="detail-back-btn" onClick={() => navigate(-1)}>←</button>
        </div>
        <div className="store-hero-bottom">
          <span className="store-category-tag">{store.category}</span>
          <h1 className="store-hero-name">{store.name}</h1>
          <p className="store-hero-loc">Floor {store.floorNumber ?? '–'} · Unit {store.unitNumber ?? '–'}</p>
        </div>
      </div>

      <div className="store-actions-bar">
        <button className="directions-btn full" onClick={openMaps}>
          <span>📍</span> Get Directions
        </button>
      </div>

      <div className="detail-tabs">
        <button className={tab === 'coupons' ? 'active' : ''} onClick={() => setTab('coupons')}>
          Deals {store.coupons?.length > 0 && <span className="tab-count">{store.coupons.length}</span>}
        </button>
        <button className={tab === 'products' ? 'active' : ''} onClick={() => setTab('products')}>
          Products {store.products?.length > 0 && <span className="tab-count">{store.products.length}</span>}
        </button>
      </div>

      <div className="detail-tab-content">
        {tab === 'coupons' && (
          <div className="coupons-list-detail">
            {store.coupons?.length === 0 && <div className="empty-tab"><p>No active deals right now.</p></div>}
            {store.coupons?.map(coupon => {
              const discount = coupon.discountType === 'PERCENTAGE' ? `${coupon.discountValue}% OFF` : `₹${coupon.discountValue} OFF`;
              const expiry = new Date(coupon.validUntil);
              const hoursLeft = Math.max(0, Math.floor((expiry - new Date()) / 3600000));
              const urgent = hoursLeft < 24;
              return (
                <div key={coupon.id} className={`coupon-detail-card ${redeemed[coupon.id] ? 'redeemed' : ''}`}>
                  <div className="coupon-detail-left">
                    <p className="coupon-detail-discount">{discount}</p>
                    <p className="coupon-detail-name">{coupon.title}</p>
                    {coupon.description && <p className="coupon-detail-desc">{coupon.description}</p>}
                    <p className={`coupon-detail-expiry ${urgent ? 'urgent' : ''}`}>
                      {urgent ? `⚡ ${hoursLeft}h left` : `Until ${expiry.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`}
                    </p>
                  </div>
                  <div className="coupon-detail-right">
                    <p className="coupon-code">{coupon.code}</p>
                    <button
                      className={`lock-deal-btn ${redeemed[coupon.id] ? 'locked' : ''}`}
                      onClick={() => redeem(coupon.id)}
                      disabled={redeemed[coupon.id] || redeeming === coupon.id}
                    >
                      {redeemed[coupon.id] ? '✓ Locked' : redeeming === coupon.id ? '…' : user ? 'Lock Deal' : 'Sign in'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {tab === 'products' && (
          <div className="products-grid-detail">
            {store.products?.length === 0 && <div className="empty-tab"><p>No products listed yet.</p></div>}
            {store.products?.map(product => (
              <div key={product.id} className="product-detail-card" onClick={() => navigate(`/product/${product.id}`)}>
                <div className="product-detail-img" style={{ backgroundImage: product.imageUrl ? `url(${product.imageUrl})` : undefined }}>
                  {!product.imageUrl && <span>📦</span>}
                </div>
                <p className="product-detail-name">{product.name}</p>
                <p className="product-detail-price">₹{product.price.toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
